import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { toast } from "sonner";
import { FolderTree, Plus, Pencil, Trash2, Check, X } from "lucide-react";

export default function CategoriesAdmin() {
  const [categories, setCategories] = useState(null);
  const [nom, setNom] = useState("");
  const [parentId, setParentId] = useState("");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api("/documents/categories").then(setCategories).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);

  const depthOf = (c, list) => {
    let depth = 0, cur = c;
    const map = Object.fromEntries(list.map((x) => [x.id, x]));
    while (cur?.parent_id && map[cur.parent_id] && depth < 5) {
      cur = map[cur.parent_id];
      depth++;
    }
    return depth;
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api("/admin/documents/categories", { method: "POST", body: { nom, parent_id: parentId || null } });
      toast.success("Catégorie créée");
      setNom("");
      setParentId("");
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (c) => {
    try {
      await api(`/admin/documents/categories/${c.id}`, {
        method: "PUT",
        body: { nom: editing.nom, parent_id: editing.parent_id || null },
      });
      toast.success("Catégorie mise à jour");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer la catégorie « ${c.nom} » ? Les documents associés seront sans catégorie.`)) return;
    try {
      await api(`/admin/documents/categories/${c.id}`, { method: "DELETE" });
      toast.success("Catégorie supprimée");
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const inputCls = "rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light";

  return (
    <AppLayout variant="admin">
      <div data-testid="admin-categories-page" className="max-w-3xl space-y-6 animate-fade-in">
        <div className="flex items-center gap-3">
          <FolderTree className="h-7 w-7 text-cfhtb-primary" />
          <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Catégories documentaires</h1>
        </div>

        <form onSubmit={create} className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-5 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-cfhtb-heading mb-1">Nouvelle catégorie</label>
            <input data-testid="category-name-input" required value={nom} onChange={(e) => setNom(e.target.value)}
              placeholder="Ex. Livre blanc" className={`${inputCls} w-full`} />
          </div>
          <div>
            <label className="block text-xs font-medium text-cfhtb-heading mb-1">Catégorie parente</label>
            <select data-testid="category-parent-select" value={parentId} onChange={(e) => setParentId(e.target.value)} className={inputCls}>
              <option value="">Aucune (racine)</option>
              {(categories || []).map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          </div>
          <button data-testid="category-add-button" disabled={busy}
            className="inline-flex items-center gap-2 bg-cfhtb-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#1f1975] disabled:opacity-60 transition-colors">
            <Plus className="h-4 w-4" /> Ajouter
          </button>
        </form>

        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          {!categories ? (
            <p className="px-4 py-10 text-center text-sm text-cfhtb-text">Chargement…</p>
          ) : categories.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-cfhtb-text" data-testid="categories-empty">Aucune catégorie.</p>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="categories-list">
              {categories.map((c) => (
                <li key={c.id} className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-cfhtb-bg/40 transition-colors" data-testid={`category-row-${c.slug}`}>
                  {editing?.id === c.id ? (
                    <div className="flex flex-wrap items-center gap-2 flex-1">
                      <input className={inputCls} value={editing.nom} data-testid="category-edit-input"
                        onChange={(e) => setEditing({ ...editing, nom: e.target.value })} />
                      <select className={inputCls} value={editing.parent_id || ""} data-testid="category-edit-parent"
                        onChange={(e) => setEditing({ ...editing, parent_id: e.target.value })}>
                        <option value="">Aucune (racine)</option>
                        {categories.filter((x) => x.id !== c.id).map((x) => <option key={x.id} value={x.id}>{x.nom}</option>)}
                      </select>
                      <button onClick={() => saveEdit(c)} data-testid="category-edit-save" className="p-1.5 text-cfhtb-success hover:bg-cfhtb-success/10 rounded-md"><Check className="h-4 w-4" /></button>
                      <button onClick={() => setEditing(null)} data-testid="category-edit-cancel" className="p-1.5 text-cfhtb-text hover:bg-slate-100 rounded-md"><X className="h-4 w-4" /></button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2" style={{ paddingLeft: `${depthOf(c, categories) * 1.5}rem` }}>
                        <span className="text-sm font-medium text-cfhtb-heading">{c.nom}</span>
                        <span className="text-xs text-cfhtb-text/60">{c.nb_documents} doc(s)</span>
                      </div>
                      <div className="flex gap-1">
                        <button data-testid={`category-edit-${c.slug}`} onClick={() => setEditing({ id: c.id, nom: c.nom, parent_id: c.parent_id || "" })}
                          className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-bg hover:text-cfhtb-primary transition-colors">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button data-testid={`category-delete-${c.slug}`} onClick={() => remove(c)}
                          className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-error/10 hover:text-cfhtb-error transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
